/**
 * A product's specifications, as the website shows them and the product form
 * keeps them (Bagz Phones, 31 Oct 2026).
 *
 * Three things the old single-value specs got wrong for a phone sold in
 * 256GB, 512GB, 1TB and 2TB:
 *   1. The spec the options are made of. "Storage" was one pick (256 GB)
 *      while the shop sells four, so the website said 256 GB on a 2TB.
 *      When the options decide a spec (Storage Capacity → Storage), the spec
 *      IS the options: all of them until the shopper picks one, then that one.
 *   2. Each option's own specs (`products.variants[i].specs`): the 2TB with a
 *      different SIM, battery or model number shows its own once picked.
 *   3. The shop's own details (`dynamicAttributes.customSpecs`, a label and a
 *      value each) for anything our list does not have.
 *
 * Pure, so the product form, the storefront and the tests share one rule.
 * Tests: ./productSpecs.test.ts.
 */

import { specText, storedVariant } from "./variantDetails";

export interface SpecRow {
  key: string;
  label: string;
  value: string;
  /** The value depends on the option picked (shown until one is). */
  varies?: true;
}

export interface CustomSpec {
  label: string;
  value: string;
}

/** The shopper's pick on the product page. */
export interface SpecPick {
  /** Canonical id of the picked option (variant-{colour}-{size}). */
  variantId?: string | null;
  /** The picked value on the options' own axis (512GB). */
  size?: string | null;
}

/** Website order and labels of the electronics specs (as the page has always shown them). */
export const SPEC_LABELS: ReadonlyArray<readonly [string, string]> = [
  ["brand", "Brand"],
  ["model", "Model"],
  ["processor", "Processor"],
  ["ram", "RAM"],
  ["storage", "Storage"],
  ["os", "Operating System"],
  ["screenSize", "Screen Size"],
  ["screenType", "Screen Type"],
  ["resolution", "Resolution"],
  ["refreshRate", "Refresh Rate"],
  ["battery", "Battery"],
  ["chargingSpeed", "Charging Speed"],
  ["chargingPort", "Charging Port"],
  ["camera", "Camera"],
  ["connectivity", "Connectivity"],
  ["simType", "SIM Type"],
  ["waterResistance", "Water Resistance"],
  ["warranty", "Warranty"],
  ["condition", "Condition"],
  ["headphoneType", "Headphone Type"],
  ["driverSize", "Driver Size"],
  ["noiseCancellation", "Noise Cancellation"],
  ["connectionType", "Connection Type"],
  ["formFactor", "Form Factor"],
  ["portCount", "Ports"],
  ["powerOutput", "Power Output"],
];

/** The per-option "model" spec is that option's model number, shown under the product's Model. */
export const MODEL_NUMBER_LABEL = "Model number";

const STORAGE_VALUE = /^\d+(\.\d+)?\s*(gb|tb)$/i;

/**
 * The spec keys an electronics product's options are made of, from the
 * options' heading (variantAxisLabel): Storage Capacity → storage, a
 * laptop's Configuration (8GB/256GB) → RAM and storage, Screen Size →
 * screenSize, a power bank's Capacity → battery. Older products saved
 * without a heading count as storage when every option reads like one.
 */
export function axisSpecKeys(axisLabel: string | undefined | null, options: readonly string[] = []): string[] {
  const l = String(axisLabel ?? "").trim().toLowerCase();
  if (!l) {
    return options.length > 0 && options.every((o) => STORAGE_VALUE.test(String(o).trim())) ? ["storage"] : [];
  }
  if (l.includes("storage")) return ["storage"];
  if (l.includes("configuration")) return ["ram", "storage"];
  if (l.includes("screen size")) return ["screenSize"];
  if (l.includes("capacity")) return ["battery"];
  return [];
}

/** One option's value for one spec key — "8GB/256GB" is RAM 8GB and storage 256GB. */
export function axisPart(key: string, axisLabel: string | undefined | null, option: string): string {
  const value = String(option ?? "").trim();
  if (String(axisLabel ?? "").toLowerCase().includes("configuration") && value.includes("/")) {
    const [ram, ...rest] = value.split("/");
    if (key === "ram") return ram.trim();
    if (key === "storage") return rest.join("/").trim();
  }
  return value;
}

function unique(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const t = v.trim();
    if (!t || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    out.push(t);
  }
  return out;
}

/** The options' values for one spec key, joined ("256GB, 512GB, 1TB, 2TB"). */
export function axisSpecValue(key: string, axisLabel: string | undefined | null, options: readonly string[]): string {
  return unique(options.map((o) => axisPart(key, axisLabel, o))).join(", ");
}

/**
 * The attributes a product form saves, with every spec the options decide
 * written as the options themselves — so the stored "Storage" never says
 * 256 GB on a phone sold in four sizes. Other specs are left as typed.
 */
export function withAxisSpecs<T extends Record<string, any>>(
  attributes: T,
  axis: { label?: string | null; options: readonly string[] } | null | undefined,
): T {
  if (!axis || axis.options.length === 0) return attributes;
  const keys = axisSpecKeys(axis.label, axis.options);
  if (keys.length === 0) return attributes;
  const next: Record<string, any> = { ...attributes };
  for (const key of keys) {
    const value = axisSpecValue(key, axis.label, axis.options);
    if (value) next[key] = value;
  }
  return next as T;
}

/** The shop's own details (a label and a value), cleaned: no blanks, no repeats, at most 20. */
export function customSpecsOf(dynamicAttributes: any): CustomSpec[] {
  const raw = dynamicAttributes?.customSpecs;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: CustomSpec[] = [];
  for (const item of raw) {
    const label = specText(item?.label).slice(0, 40);
    const value = specText(item?.value).slice(0, 120);
    if (!label || !value || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    out.push({ label, value });
    if (out.length >= 20) break;
  }
  return out;
}

/**
 * The attributes a product form saves (`dynamicAttributes`, and the
 * electronics specs built from them): the specs the options decide written
 * as the options, and the shop's own details without half-typed rows.
 */
export function attributesForSave<T extends Record<string, any>>(
  attributes: T,
  axis: { label?: string | null; options: readonly string[] } | null | undefined,
): T {
  const next: Record<string, any> = { ...withAxisSpecs(attributes, axis) };
  if ("customSpecs" in next) {
    const clean = customSpecsOf(next);
    if (clean.length > 0) next.customSpecs = clean;
    else delete next.customSpecs;
  }
  return next as T;
}

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "detail";

/**
 * The rows of the website's Specifications card for the shopper's pick.
 *
 * - A spec the options decide shows every option until one is picked, then
 *   the picked one.
 * - A spec an option overrides shows that option's value once it is picked;
 *   before that, the values the options have between them (up to four,
 *   marked `varies`).
 * - An option's model number shows as its own row under Model.
 * - The shop's own details come last, in the order typed.
 */
export function specRowsFor(product: any, pick: SpecPick = {}): SpecRow[] {
  const ea = product?.electronicsAttributes ?? {};
  const options: string[] = (Array.isArray(ea.storageOptions) ? ea.storageOptions : [])
    .map((o: unknown) => String(o ?? "").trim())
    .filter(Boolean);
  const axisLabel: string | undefined = ea.variantAxisLabel;
  const axisKeys = options.length > 0 ? axisSpecKeys(axisLabel, options) : [];
  const variants: any[] = Array.isArray(product?.variants) ? product.variants : [];
  const picked = storedVariant(product, pick.variantId);
  const pickedSize = String(pick.size ?? "").trim();

  const rows: SpecRow[] = [];
  for (const [key, label] of SPEC_LABELS) {
    const base = specText(key === "condition" ? ea.condition : ea[key]);

    if (axisKeys.includes(key)) {
      if (pickedSize) {
        const value = axisPart(key, axisLabel, pickedSize);
        if (value) rows.push({ key, label, value });
      } else {
        const all = unique(options.map((o) => axisPart(key, axisLabel, o)));
        if (all.length > 0) rows.push({ key, label, value: all.join(", "), ...(all.length > 1 ? { varies: true as const } : {}) });
      }
      continue;
    }

    if (key === "model") {
      if (base) rows.push({ key, label, value: base });
      const own = specText(picked?.specs?.model);
      if (own && own.toLowerCase() !== base.toLowerCase()) {
        rows.push({ key: "modelNumber", label: MODEL_NUMBER_LABEL, value: own });
      }
      continue;
    }

    if (pick.variantId) {
      // An option is picked: its own value, else the product's.
      const value = specText(picked?.specs?.[key]) || base;
      if (value) rows.push({ key, label, value });
      continue;
    }

    // Nothing picked yet: the product's value, unless options differ.
    const overridden = variants.some((v) => specText(v?.specs?.[key]));
    if (!overridden) {
      if (base) rows.push({ key, label, value: base });
      continue;
    }
    const each = variants.map((v) => specText(v?.specs?.[key]) || base);
    const all = unique(each);
    const someHaveNone = each.some((v) => !v);
    if (all.length === 0) continue;
    const varies = all.length > 1 || someHaveNone;
    const value = all.length <= 4 ? all.join(" / ") : base || all.slice(0, 4).join(" / ");
    rows.push({ key, label, value, ...(varies ? { varies: true as const } : {}) });
  }

  for (const c of customSpecsOf(product?.dynamicAttributes)) {
    rows.push({ key: `custom-${slug(c.label)}`, label: c.label, value: c.value });
  }
  return rows;
}

/**
 * Which option the shopper has picked on an electronics product page, as a
 * canonical variant id. A product without colours sells its options on the
 * "default" colour (variant-default-512GB); one without options sells a
 * colour on "default" (variant-{colour}-default). Undefined until enough is
 * picked to name one option.
 */
export function pickedElectronicsVariantId(input: {
  colorId?: string | null;
  hasColours: boolean;
  size?: string | null;
  hasSizes: boolean;
}): string | undefined {
  const colour = input.hasColours ? input.colorId || undefined : "default";
  if (!colour) return undefined;
  const size = input.hasSizes ? input.size || undefined : "default";
  if (!size) return undefined;
  return `variant-${colour}-${size}`.replace(/[^\x20-\x7E]/g, "-").replace(/-{2,}/g, "-");
}
